import { Link } from 'react-router-dom';
import { Icon } from '@/components/ui/Icon';
import { PinkDot } from '@/components/brand/PinkDot';
import { SectionMarker } from '@/components/brand/SectionMarker';
import { StatusDot } from '@/components/ui/StatusDot';
import { ROUTES } from '@/config/routes';
import { useProfiles } from '@/modules/profiles/useProfiles';
import { cn } from '@/lib/cn';

/**
 * Desktop right rail — HEAT signal, world continuity, nightlife pulse.
 * Restrained. Editorial. Not a dashboard.
 * Hidden < lg.
 */
export const SideRail = () => {
  const { profiles } = useProfiles();
  const live = profiles.filter((p) => p.presence === 'live');
  const pulse = profiles.filter((p) => p.band === 'pulse');

  return (
    <aside
      className={cn(
        'sticky top-16 hidden lg:flex',
        'h-[calc(100dvh-4rem)] w-80 flex-col gap-8',
        'border-l border-white/[0.07] bg-ink px-7 py-8',
      )}
    >
      {/* HEAT */}
      <section>
        <div className="mb-5 flex items-center justify-between">
          <SectionMarker>Heat tonight</SectionMarker>
          <span className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
            {String(pulse.length).padStart(2, '0')} pulsing
          </span>
        </div>
        <ul className="space-y-1">
          {live.slice(0, 5).map((p, i) => (
            <li key={p.id}>
              <Link
                to={ROUTES.profile(p.handle)}
                className={cn(
                  'group flex items-center gap-3 py-2.5',
                  'border-b border-white/[0.04] last:border-b-0',
                  'transition-colors duration-fast ease-signal',
                )}
              >
                <span className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted group-hover:text-pink">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="flex-1 font-display text-sm font-bold uppercase tracking-wide text-text-primary group-hover:text-pink">
                  @{p.handle}
                </span>
                <span className="text-[10px] uppercase tracking-widest text-text-muted">
                  {p.distance}
                </span>
                <StatusDot status="live" size="h-1.5 w-1.5" />
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {/* World continuity */}
      <section>
        <SectionMarker className="mb-4" muted>
          One profile · every version
        </SectionMarker>
        <Link
          to={ROUTES.world}
          className={cn(
            'block rounded-sharp border border-white/[0.07] bg-charcoal/60 p-4',
            'transition-colors duration-fast ease-signal',
            'hover:border-pink/40',
          )}
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="font-display text-[10px] font-bold uppercase tracking-widest text-pink">
                HOTTR ID
              </p>
              <p className="mt-1 font-mono text-[11px] text-text-secondary">
                hid_8f4a-22cd-9e10
              </p>
            </div>
            <Icon name="fingerprint" size={20} className="text-pink" />
          </div>
          <p className="mt-3 text-xs text-text-secondary">
            Identity layer. Not a destination.
          </p>
        </Link>
      </section>

      {/* Hottie */}
      <section className="mt-auto">
        <div
          className={cn(
            'flex items-center gap-3 rounded-sharp border border-white/[0.07] bg-charcoal/60 px-4 py-3',
          )}
        >
          <PinkDot pulse size="h-1.5 w-1.5" />
          <div className="min-w-0 flex-1">
            <p className="font-display text-[10px] font-bold uppercase tracking-widest text-text-primary">
              Hottie · concierge
            </p>
            <p className="truncate text-[11px] text-text-secondary">
              one word. i&apos;ll route you.
            </p>
          </div>
          <Icon name="bolt" size={16} className="text-pink" />
        </div>
      </section>
    </aside>
  );
};
